//----------------------------------------------------------------------------------------------------------------------
// Raycast Utilities
//----------------------------------------------------------------------------------------------------------------------

import type { AbstractMesh, Camera, PickingInfo, Ray, Scene, TransformNode, Vector3 } from '@babylonjs/core';

import type { GameEntity } from '../classes/entity.ts';
import type { GameEntityManager } from '../managers/entity.ts';

//----------------------------------------------------------------------------------------------------------------------

export interface EntityPickResult
{
    entity : GameEntity;
    point : Vector3;
    distance : number;
    normal : Vector3;
    mesh : AbstractMesh;
    pickingInfo : PickingInfo;
}

export interface EntityPickFilter
{
    type ?: string;
    tags ?: string[];
    predicate ?: (entity : GameEntity) => boolean;
}

//----------------------------------------------------------------------------------------------------------------------

export class RaycastHelper
{
    private _entityManager : GameEntityManager;

    constructor(entityManager : GameEntityManager)
    {
        this._entityManager = entityManager;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public API
    //------------------------------------------------------------------------------------------------------------------

    pickEntity(scene : Scene, x : number, y : number, filter ?: EntityPickFilter) : EntityPickResult | null
    {
        const info = scene.pick(x, y);
        return this._toEntityResult(info, filter);
    }

    pickEntities(scene : Scene, x : number, y : number, filter ?: EntityPickFilter) : EntityPickResult[]
    {
        const infos = scene.multiPick(x, y);
        if(!infos) { return []; }

        const results : EntityPickResult[] = [];
        for(const info of infos)
        {
            const result = this._toEntityResult(info, filter);
            if(result)
            {
                results.push(result);
            }
        }
        return results;
    }

    pickEntityWithRay(scene : Scene, ray : Ray, filter ?: EntityPickFilter) : EntityPickResult | null
    {
        const info = scene.pickWithRay(ray);
        if(!info) { return null; }
        return this._toEntityResult(info, filter);
    }

    pickEntitiesWithRay(scene : Scene, ray : Ray, filter ?: EntityPickFilter) : EntityPickResult[]
    {
        const infos = scene.multiPickWithRay(ray);
        if(!infos) { return []; }

        const results : EntityPickResult[] = [];
        for(const info of infos)
        {
            const result = this._toEntityResult(info, filter);
            if(result)
            {
                results.push(result);
            }
        }
        return results;
    }

    pickEntityForward(
        scene : Scene,
        camera : Camera,
        maxDistance = 1000,
        filter ?: EntityPickFilter
    ) : EntityPickResult | null
    {
        const ray = camera.getForwardRay(maxDistance);
        return this.pickEntityWithRay(scene, ray, filter);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Private
    //------------------------------------------------------------------------------------------------------------------

    private _toEntityResult(info : PickingInfo, filter ?: EntityPickFilter) : EntityPickResult | null
    {
        if(!info.hit || !info.pickedMesh || !info.pickedPoint)
        {
            return null;
        }

        const mesh = info.pickedMesh;

        // Look up entity on the picked mesh, then walk up parent chain
        let entity = this._entityManager.getByNode(mesh as unknown as TransformNode);
        if(!entity)
        {
            let current : TransformNode | null = (mesh as unknown as TransformNode).parent as TransformNode | null;
            while(current)
            {
                entity = this._entityManager.getByNode(current);
                if(entity) { break; }
                current = current.parent as TransformNode | null;
            }
        }

        if(!entity)
        {
            return null;
        }

        // Apply filter
        if(filter)
        {
            if(filter.type && entity.type !== filter.type)
            {
                return null;
            }

            if(filter.tags)
            {
                for(const tag of filter.tags)
                {
                    if(!entity.hasTag(tag))
                    {
                        return null;
                    }
                }
            }

            if(filter.predicate && !filter.predicate(entity))
            {
                return null;
            }
        }

        const normal = info.getNormal(true, true);

        return {
            entity,
            point: info.pickedPoint,
            distance: info.distance,
            normal: normal ?? { x: 0, y: 0, z: 0 } as unknown as Vector3,
            mesh,
            pickingInfo: info,
        };
    }
}

//----------------------------------------------------------------------------------------------------------------------
